import React, { useState, useEffect, useMemo } from "react";
import { api } from "@/lib/api";
import { PageTitle, Fab, RowActions, EmptyState } from "@/components/shared";
import DataDialog from "@/components/DataDialog";
import { ANIMALS, getAnimal } from "@/lib/animals";
import { formatDate } from "@/lib/format";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const animalOptions = ANIMALS.map((a) => ({ value: a.key, label: `${a.emoji} ${a.name} — ${a.cat}` }));

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [pasangan, setPasangan] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");

  const load = async () => {
    const [u, p] = await Promise.all([api.get("/users"), api.get("/pasangan")]);
    setUsers(u.data); setPasangan(p.data);
  };
  useEffect(() => { load(); }, []);

  const fields = [
    { name: "email", label: "Email", type: "text", required: true },
    { name: "username", label: "Username", type: "text", required: true },
    { name: "password", label: editing ? "Password (kosongkan jika tetap)" : "Password", type: "text", required: !editing },
    { name: "role", label: "Role", type: "select", options: ["Pria", "Wanita", "Admin", "sysadmin"], required: true },
    { name: "pasangan_id", label: "Pasangan (opsional)", type: "select", options: pasangan.map((p) => ({ value: p.id, label: `${p.nama} (${p.id})` })) },
    { name: "animal", label: "Karakter Hewan", type: "select", options: animalOptions, required: true },
  ];

  const onSave = async (payload) => {
    if (editing) {
      const body = { ...payload };
      if (!body.password) delete body.password;
      await api.put(`/users/${editing.id}`, body);
      toast.success("User diperbarui");
    } else {
      await api.post("/users", payload);
      toast.success("User ditambahkan");
    }
    await load();
  };

  const del = async (id) => { await api.delete(`/users/${id}`); toast.success("User dihapus"); load(); };
  const toggle = async (u) => { await api.put(`/users/${u.id}`, { active: !u.active }); load(); };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return [...users].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""))
      .filter((u) => !q || (u.email || "").toLowerCase().includes(q) || (u.username || "").toLowerCase().includes(q) || (u.role || "").toLowerCase().includes(q));
  }, [users, search]);

  return (
    <div>
      <PageTitle title="User" subtitle="Kelola akun pengguna (sysadmin)" />
      <Input data-testid="user-search" placeholder="Cari email / username / role..." value={search} onChange={(e) => setSearch(e.target.value)} className="mb-4 max-w-sm" />
      <div className="rounded-xl border border-border">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Email</TableHead><TableHead>Username</TableHead><TableHead>Role</TableHead>
            <TableHead>Pasangan</TableHead><TableHead>Hewan</TableHead><TableHead>Aktif</TableHead>
            <TableHead>Dibuat</TableHead><TableHead className="text-right">Aksi</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.map((u) => (
              <TableRow key={u.id} data-testid={`row-${u.id}`}>
                <TableCell>{u.email}</TableCell>
                <TableCell className="font-medium">{u.username}</TableCell>
                <TableCell><Badge variant="outline">{u.role}</Badge></TableCell>
                <TableCell>{u.pasangan_id || "-"}</TableCell>
                <TableCell>{u.animal ? `${getAnimal(u.animal).emoji} ${getAnimal(u.animal).name}` : "-"}</TableCell>
                <TableCell><Switch data-testid={`toggle-${u.id}`} checked={!!u.active} onCheckedChange={() => toggle(u)} /></TableCell>
                <TableCell className="text-xs text-muted-foreground">{formatDate(u.created_at)}</TableCell>
                <TableCell><RowActions id={u.id} onEdit={() => { setEditing(u); setOpen(true); }} onDelete={() => del(u.id)} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {filtered.length === 0 && <EmptyState text="Tidak ada user." />}
      </div>
      <Fab testId="fab-user" onClick={() => { setEditing(null); setOpen(true); }} />
      <DataDialog open={open} onOpenChange={setOpen} title={editing ? "Edit User" : "Tambah User"} fields={fields} initial={editing} onSave={onSave} />
    </div>
  );
}
